// Verbesserte DIN 5008 Telefonnummer-Formatierung für das Frontend
import { GERMAN_AREA_CODES } from './germanAreaCodes';

export function formatPhoneNumberDIN5008(phoneNumber: string): string {
  if (!phoneNumber || typeof phoneNumber !== 'string') return '';
  
  // Entferne alle nicht-numerischen Zeichen außer +
  let cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // Wenn leer, gib leeren String zurück
  if (!cleaned) return '';
  
  // Internationale deutsche Nummern normalisieren
  if (cleaned.startsWith('+49')) {
    cleaned = '0' + cleaned.substring(3);
  } else if (cleaned.startsWith('0049')) {
    cleaned = '0' + cleaned.substring(4);
  } else if (cleaned.startsWith('49') && cleaned.length > 10) {
    cleaned = '0' + cleaned.substring(2);
  }
  
  // Nur deutsche Nummern (mit 0 beginnend) oder internationale Nummern
  if (cleaned.startsWith('0')) {
    // Deutsche Nummer
    return formatGermanNumber(cleaned);
  } else if (cleaned.startsWith('+')) {
    // Internationale Nummer
    return formatInternationalNumber(cleaned);
  }
  
  // Fallback: Original-Nummer zurückgeben, wenn keine Regel passt
  return phoneNumber;
}

function formatGermanNumber(number: string): string {
  // Deutsche Mobilnummern (015x, 016x, 017x)
  if (number.match(/^(015|016|017)\d{8,9}$/)) {
    if (number.length === 11) {
      // Format: 0170 1234567
      return number.replace(/^(\d{4})(\d+)$/, '$1 $2');
    } else if (number.length === 12) {
      // Format: 0170 12345678
      return number.replace(/^(\d{4})(\d+)$/, '$1 $2');
    }
  }
  
  // Deutsche Festnetznummern - verwende die Vorwahlen-Liste
  if (number.length >= 7) {
    // Suche nach passender Vorwahl (längste zuerst)
    for (const areaCode of GERMAN_AREA_CODES) {
      if (number.startsWith(areaCode)) {
        const remainingNumber = number.substring(areaCode.length);
        
        // Prüfe ob genug Ziffern für eine gültige Telefonnummer übrig sind
        if (remainingNumber.length >= 3 && remainingNumber.length <= 10) {
          // Formatiere die Rufnummer in Gruppen
          const formattedRemaining = formatRemainingNumber(remainingNumber);
          return `${areaCode} ${formattedRemaining}`;
        }
      }
    }
  }
  
  // Fallback für kurze Nummern oder nicht erkannte Vorwahlen
  if (number.length <= 6) {
    // Sehr kurze Nummern (möglicherweise interne Nummern)
    return number;
  }
  
  // Standard-Fallback: 4-stellige Vorwahl annehmen
  if (number.length >= 8) {
    const areaCode = number.substring(0, 4);
    const remainingNumber = number.substring(4);
    const formattedRemaining = formatRemainingNumber(remainingNumber);
    return `${areaCode} ${formattedRemaining}`;
  }
  
  return number;
}

function formatRemainingNumber(number: string): string {
  if (number.length <= 4) {
    return number;
  } else if (number.length <= 8) {
    // Teile in 2 Gruppen
    const mid = Math.ceil(number.length / 2);
    return `${number.substring(0, mid)} ${number.substring(mid)}`;
  } else {
    // Teile in 3er-Gruppen
    const groups = [];
    for (let i = 0; i < number.length; i += 3) {
      groups.push(number.substring(i, i + 3));
    }
    return groups.join(' ');
  }
}

function formatInternationalNumber(number: string): string {
  // Andere internationale Nummern
  const match = number.match(/^\+(\d{1,3})(\d{3,4})(\d+)$/);
  if (match) {
    return `+${match[1]} ${match[2]} ${match[3]}`;
  }
  
  return number;
}

// Prüfe ob eine Telefonnummer erfolgreich formatiert werden konnte
export function canFormatPhoneNumber(phoneNumber: string): boolean {
  if (!phoneNumber || typeof phoneNumber !== 'string') return true; // Leere Nummern sind OK
  
  const original = phoneNumber.trim();
  
  // Einfache Validierung: deutsche Handynummern (015x, 016x, 017x) oder Festnetz (0xxxx)
  // Erlaubt auch bereits formatierte Nummern mit Leerzeichen
  const cleanedNumber = original.replace(/[^\d+]/g, '');
  
  console.log(`🔍 Testing phone number: '${original}' -> cleaned: '${cleanedNumber}'`);
  
  // Deutsche Mobilnummern (015x, 016x, 017x) - korrigierte Länge
  if (cleanedNumber.match(/^(015|016|017)\d{7,9}$/)) {
    console.log(`✅ Erkannt als deutsche Mobilnummer: ${cleanedNumber}`);
    return true;
  }
  
  // Deutsche Festnetznummern (mindestens 6 Ziffern)
  if (cleanedNumber.match(/^0\d{5,}$/)) {
    console.log(`✅ Erkannt als deutsche Festnetznummer: ${cleanedNumber}`);
    return true;
  }
  
  // Internationale Nummern mit +
  if (cleanedNumber.match(/^\+\d{7,}$/)) {
    console.log(`✅ Erkannt als internationale Nummer: ${cleanedNumber}`);
    return true;
  }
  
  // Sehr kurze Nummern (interne Durchwahlen etc.)
  if (cleanedNumber.length <= 6) {
    console.log(`✅ Erkannt als kurze Nummer: ${cleanedNumber}`);
    return true;
  }
  
  // Fallback: wenn die Nummer bereits Leerzeichen enthält, ist sie wahrscheinlich schon formatiert
  if (original.includes(' ') || original.includes('-')) {
    console.log(`✅ Erkannt als bereits formatierte Nummer: ${original}`);
    return true;
  }
  
  console.log(`❌ Telefonnummer nicht erkannt: ${original} (${cleanedNumber})`);
  return false;
}
